import { Locale } from '@finxone-platform/shared/enums';
import { AlertTypes } from '@finxone-platform/shared/sys-config-types';
import { BeneficiaryErrorCodes } from '../error-codes/beneficiaries.type';
import { BeneficiariesSuccessCodes } from '../success-codes/beneficiaries.type';
import { SupportedToasterLocales } from './general.utils';

const defaultBeneficiariesAlerts = {
  FAILED_TO_FETCH_CHECK_PAYEE_TITLE: 'Unable To Verify Beneficiary',
  FAILED_TO_FETCH_CHECK_PAYEE_DESCRIPTION: 'Unable to verify beneficiary details. Please try again later.',
  FAILED_TO_UPDATE_BENEFICIARY_TITLE: 'Beneficiary Update Failed',
  FAILED_TO_UPDATE_BENEFICIARY_DESCRIPTION:
    'Unable to update beneficiary details. Please check the details carefully and try again.',
  FAILED_TO_CREATE_BENEFICIARY_TITLE: 'Failed To Create Beneficiary',
  FAILED_TO_CREATE_BENEFICIARY_DESCRIPTION:
    'There was an error setting up the beneficiary. Please check the details carefully and try again.',
  INVALID_IBAN_TITLE: 'Invalid IBAN',
  INVALID_IBAN_DESCRIPTION: 'The IBAN entered is invalid. Please check the details carefully and try again.',
  INVALID_BIC_TITLE: 'Invalid BIC',
  INVALID_BIC_DESCRIPTION:
    'The SWIFT/BIC entered is invalid. Please check the details carefully and try again.',
  INVALID_ACCOUNT_NUMBER_TITLE: 'Invalid Account Number',
  INVALID_ACCOUNT_NUMBER_DESCRIPTION:
    'The account number is invalid. Please check the details carefully and try again.',
  INVALID_SORT_CODE_TITLE: 'Invalid Sort Code',
  INVALID_SORT_CODE_DESCRIPTION:
    'The sort code is invalid. Please check the details carefully and try again.',
  ADD_BENEFICIARY_TITLE: 'New Beneficiary Added',
  ADD_BENEFICIARY_DESCRIPTION: 'Beneficiary {{beneficiaryName}} set up successfully.',
  UPDATE_BENEFICIARY_TITLE: 'Beneficiary Details Updated',
  UPDATE_BENEFICIARY_DESCRIPTION: 'Account details for {{beneficiaryName}} updated successfully.',
  REMOVE_BENEFICIARY_TITLE: 'Beneficiary Removed',
  REMOVE_BENEFICIARY_DESCRIPTION: '{{beneficiaryName}} has been removed successfully.',
  ADD_FAVORITE_BENEFICIARY_TITLE: 'Marked as Favorite',
  ADD_FAVORITE_BENEFICIARY_DESCRIPTION: 'The beneficiary has been successfully added to your favorites list.',
  REMOVE_FAVORITE_BENEFICIARY_TITLE: 'Removed from Favorites',
  REMOVE_FAVORITE_BENEFICIARY_DESCRIPTION:
    'The beneficiary has been successfully removed from favorites list.',
  ALREADY_FAVORITE_TITLE: 'Already a Favorite',
  ALREADY_FAVORITE_DESCRIPTION: 'This beneficiary is already in your favorites list.',
  NOT_IN_FAVORITE_TITLE: 'Not in Favorites',
  NOT_IN_FAVORITE_DESCRIPTION: 'This beneficiary is not currently marked as a favorite.',
  FAILED_TO_MARK_FAVORITE_TITLE: 'Failed to Mark Favorite',
  FAILED_TO_MARK_FAVORITE_DESCRIPTION: 'There was an error marking the beneficiary as a favorite.',
  BENEFICIARY_NOT_FOUND_TITLE: 'Beneficiary Not Found',
  BENEFICIARY_NOT_FOUND_DESCRIPTION: 'The beneficiary was not found.',
  INVALID_ACCOUNT_DETAILS_COP_TITLE: 'Invalid Account Details',
  INVALID_ACCOUNT_DETAILS_COP_DESCRIPTION:
    'The account number / sort code provided are invalid. Please check these details and try again.',
};

export const beneficiariesTranslations: {
  // For new locales just add them here to the extract
  [key in SupportedToasterLocales]: typeof defaultBeneficiariesAlerts;
} = {
  [Locale.en_GB]: defaultBeneficiariesAlerts,
  // Add more locales as needed
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateBeneficiariesAlerts = (translations: typeof defaultBeneficiariesAlerts) => ({
  [BeneficiaryErrorCodes.FAILED_TO_FETCH_CHECK_PAYEE]: {
    name: 'Failed to fetch beneficiary verification',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_FETCH_CHECK_PAYEE_TITLE,
    description: translations.FAILED_TO_FETCH_CHECK_PAYEE_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.FAILED_TO_UPDATE_BENEFICIARY]: {
    name: 'Failed to update beneficiary details!',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_UPDATE_BENEFICIARY_TITLE,
    description: translations.FAILED_TO_UPDATE_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.FAILED_TO_CREATE_BENEFICIARY]: {
    name: 'Failed to create beneficiary details!',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_CREATE_BENEFICIARY_TITLE,
    description: translations.FAILED_TO_CREATE_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiariesSuccessCodes.ADD_BENEFICIARY]: {
    name: 'Beneficiary created!',
    type: AlertTypes.SUCCESS,
    title: translations.ADD_BENEFICIARY_TITLE,
    description: translations.ADD_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiariesSuccessCodes.UPDATE_BENEFICIARY]: {
    name: 'Beneficiary updated!',
    type: AlertTypes.SUCCESS,
    title: translations.UPDATE_BENEFICIARY_TITLE,
    description: translations.UPDATE_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiariesSuccessCodes.REMOVE_BENEFICIARY]: {
    name: 'Beneficiary removed!',
    type: AlertTypes.SUCCESS,
    title: translations.REMOVE_BENEFICIARY_TITLE,
    description: translations.REMOVE_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.INVALID_IBAN]: {
    name: 'Invalid IBAN!',
    type: AlertTypes.ERROR,
    title: translations.INVALID_IBAN_TITLE,
    description: translations.INVALID_IBAN_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.INVALID_BIC]: {
    name: 'Invalid BIC!',
    type: AlertTypes.ERROR,
    title: translations.INVALID_BIC_TITLE,
    description: translations.INVALID_BIC_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.INVALID_ACCOUNT_NUMBER]: {
    name: 'Invalid Account Number!',
    type: AlertTypes.ERROR,
    title: translations.INVALID_ACCOUNT_NUMBER_TITLE,
    description: translations.INVALID_ACCOUNT_NUMBER_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.INVALID_SORT_CODE]: {
    name: 'Invalid Sort Code!',
    type: AlertTypes.ERROR,
    title: translations.INVALID_SORT_CODE_TITLE,
    description: translations.INVALID_SORT_CODE_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.ALREADY_FAVORITE]: {
    name: 'Already a Favorite',
    type: AlertTypes.ERROR,
    title: translations.ALREADY_FAVORITE_TITLE,
    description: translations.ALREADY_FAVORITE_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.NOT_IN_FAVORITE]: {
    name: 'Not in Favorites!',
    type: AlertTypes.ERROR,
    title: translations.NOT_IN_FAVORITE_TITLE,
    description: translations.NOT_IN_FAVORITE_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.FAILED_TO_MARK_FAVORITE]: {
    name: 'Failed to Mark Favorite!',
    type: AlertTypes.ERROR,
    title: translations.FAILED_TO_MARK_FAVORITE_TITLE,
    description: translations.FAILED_TO_MARK_FAVORITE_DESCRIPTION,
  },
  [BeneficiariesSuccessCodes.ADD_FAVORITE_BENEFICIARY]: {
    name: 'Marked as Favorite',
    type: AlertTypes.SUCCESS,
    title: translations.ADD_FAVORITE_BENEFICIARY_TITLE,
    description: translations.ADD_FAVORITE_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiariesSuccessCodes.REMOVE_FAVORITE_BENEFICIARY]: {
    name: 'Removed from Favorites',
    type: AlertTypes.SUCCESS,
    title: translations.REMOVE_FAVORITE_BENEFICIARY_TITLE,
    description: translations.REMOVE_FAVORITE_BENEFICIARY_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.BENEFICIARY_NOT_FOUND]: {
    name: 'Beneficiary Not Found!',
    type: AlertTypes.ERROR,
    title: translations.BENEFICIARY_NOT_FOUND_TITLE,
    description: translations.BENEFICIARY_NOT_FOUND_DESCRIPTION,
  },
  [BeneficiaryErrorCodes.INVALID_ACCOUNT_DETAILS_COP]: {
    name: 'Invalid Account Details COP',
    type: AlertTypes.ERROR,
    title: translations.INVALID_ACCOUNT_DETAILS_COP_TITLE,
    description: translations.INVALID_ACCOUNT_DETAILS_COP_DESCRIPTION,
  },
});
